import React, { useEffect, useState, useRef } from "react";
import driver from "./assets/images/driver.png";
import { IoMdStarOutline } from "react-icons/io";
import { FaPhoneAlt } from "react-icons/fa";
import { RiMessage2Fill } from "react-icons/ri";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { supabase } from "../supabase";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { FaPencilAlt } from "react-icons/fa";
import L from "leaflet";
import { useMap } from "react-leaflet";
import { CiLock } from "react-icons/ci";
import { FaLock } from "react-icons/fa6";
import { FaShieldHalved } from "react-icons/fa6";
import { FaBoltLightning } from "react-icons/fa6";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { formatINR } from "../utils/currency";
import { toast } from "react-toastify";
import Login from "./Login";
import GoToTop from "../GoToTop";
import { GST } from "../API_KEYS";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Tooltip,
} from "react-leaflet";
import payImg from "./assets/images/payment-methods.webp";
import { RAZORPAY_KEY } from "../API_KEYS";

function RideDetails() {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const [driverDetails, setDriverDetails] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);

  const [showHelpModal, setShowHelpModal] = useState(false);

  const [showBookingSelector, setShowBookingSelector] = useState(false);

  const [directionsResult, setDirectionsResult] = useState(null);

  // Auth user
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsub();
  }, []);

  // Fetch booking
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, "LiveBookings"),
      where("customer_id", "==", user.uid),
    );

    const unsub = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const allBookings = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setBookings(allBookings);

        // 🔥 KEEP SELECTED BOOKING UPDATED
        if (booking) {
          const updatedBooking = allBookings.find((b) => b.id === booking.id);
          if (updatedBooking) {
            // setBooking(updatedBooking);
            setBooking({ ...updatedBooking });
          }
        } else if (allBookings.length == 1) {
          setBooking(allBookings[0]);
        }
      } else {
        setBookings([]);
        setBooking(null);
      }

      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (
      booking?.pickup_lat &&
      booking?.pickup_lng &&
      booking?.drop_lat &&
      booking?.drop_lng
    ) {
      const fetchRoute = async () => {
        try {
          const url = `https://router.project-osrm.org/route/v1/driving/${booking.pickup_lng},${booking.pickup_lat};${booking.drop_lng},${booking.drop_lat}?overview=full&geometries=geojson`;
          const res = await fetch(url);
          const data = await res.json();

          if (data.routes && data.routes.length > 0) {
            const coords = data.routes[0].geometry.coordinates;

            // Convert [lng, lat] → [lat, lng]
            const formattedCoords = coords.map(([lng, lat]) => [lat, lng]);

            setDirectionsResult(formattedCoords);
          }
        } catch (err) {
          console.error("Route fetch error:", err);
        }
      };

      fetchRoute();
    }
  }, [booking]);

  useEffect(() => {
    if (!booking?.driver_id) return;

    const fetchDriver = async () => {
      const { data, error } = await supabase
        .from("Drivers")
        .select("*")
        .eq("id", booking.driver_id)
        .single();

      if (!error) {
        setDriverDetails(data);
      } else {
        console.error("Driver fetch error:", error);
      }
    };

    fetchDriver();
  }, [booking?.driver_id]);

  useEffect(() => {
    if (!booking?.vehicle_id) return;

    const fetchVehicle = async () => {
      const { data, error } = await supabase
        .from("Vehicles")
        .select("*")
        .eq("id", booking.vehicle_id)
        .single();

      if (!error) {
        setVehicleDetails(data);
      } else {
        console.error("Vehicle fetch error:", error);
      }
    };

    fetchVehicle();
  }, [booking?.vehicle_id]);

  const handleCancelRide = async (booking) => {
    if (!booking) return;

    if (!window.confirm("Are you sure you want to cancel this ride?")) return;

    try {
      // Prepare only the fields you want
      const bookingData = {
        accepted_at: booking.accepted_at
          ? new Date(booking.accepted_at).toISOString()
          : null,
        base_price: booking.base_price?.toString() || "0",
        created_at: booking.createdAt
          ? new Date(booking.createdAt).toISOString()
          : new Date().toISOString(),
        customer_id: booking.customer_id || "",
        customer_name: booking.customer_name || "",
        customer_phone: booking.customer_phone || "",
        distance: booking.distance?.toString() || "0",
        driver_id: booking.driver_id || 0,
        drop: booking.drop || "",
        duration: booking.duration?.toString() || "0",
        isScheduled: booking.isScheduled || false,
        km_price: booking.km_price?.toString() || "0",
        otp_verified: booking.otp_verified || false,
        passengers: booking.passengers || 1,
        pickup: booking.pickup || "",
        pickupDate: booking.pickupDate || null,
        pickupTime: booking.pickupTime || null,
        ride_otp: booking.ride_otp || 0,
        start_lat: booking.driver_lat || 0,
        start_lng: booking.driver_lng || 0,
        status: 5,
        total_price: booking.total_price?.toString() || "0",
        updated_at: new Date().toISOString(),
        vehicle_id: booking.vehicle_id || 0,
        cancelled_at: new Date().toISOString(),
      };

      // Insert into Supabase "Bookings" table
      const { data, error } = await supabase
        .from("Bookings")
        .insert([bookingData]);

      if (error) {
        console.error("Failed to save cancelled booking in Supabase:", error);
        alert("Failed to cancel ride. Please try again.");
        return; // stop here, do NOT delete from Firestore
      }

      // Delete from Firestore only if Supabase insert succeeds
      const docRef = doc(db, "LiveBookings", booking.id);
      await deleteDoc(docRef);

      toast.success("Ride cancelled successfully!");
      // navigate or update UI as needed
    } catch (err) {
      console.error(err);
      alert("Failed to cancel ride. Please try again.");
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";

    let date;

    if (typeof timestamp?.toDate === "function") {
      date = timestamp.toDate();
    } else if (typeof timestamp?.seconds === "number") {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const getDropDateFromDays = () => {
    if (!booking?.pickupTime || !booking?.NoOfDays) return "N/A";

    let pickup;

    if (booking.pickupTime.toDate) {
      pickup = booking.pickupTime.toDate();
    } else {
      pickup = new Date(booking.pickupTime);
    }

    const days = parseInt(booking.NoOfDays, 10) - 1;

    if (isNaN(days)) return "N/A";

    pickup.setDate(pickup.getDate() + days);

    return pickup.toLocaleDateString("en-IN", {
      dateStyle: "medium",
    });
  };

  const calculateRideDays = (start, end) => {
    if (!start || !end) return 0;

    // Convert Firestore Timestamp to JS Date
    const startDate = start?.seconds
      ? new Date(start.seconds * 1000)
      : new Date(start);

    const endDate = end?.seconds ? new Date(end.seconds * 1000) : new Date(end);

    const diffInMs = endDate - startDate;

    if (diffInMs <= 0) return 0;

    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

    return diffInDays;
  };

  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      toast.success("Logged in successfully!");
    } catch (error) {
      console.error("Google Login Error:", error);
      toast.error("Login failed. Try again.");
    }
  };

  function FitBounds({ coords }) {
    const map = useMap();

    useEffect(() => {
      if (coords && coords.length > 0) {
        setTimeout(() => {
          const bounds = L.latLngBounds(coords);
          map.fitBounds(bounds, { padding: [50, 50] });
        }, 300); // delay is important
      }
    }, [coords, map]);

    return null;
  }

  const days = calculateRideDays(booking?.ride_start, booking?.ride_end);

  const dailyRate = Number(booking?.daily_rate || 0);
  const kmPrice = Number(booking?.km_price || 0);
  const meter = Number(booking?.meter_reading || 0);
  const advance = Number(booking?.preBookingFee || 0);
  const totalPrice = Number(booking?.total_price || 0);

  let finalAmount = 0;

  if (booking?.booking_type == "Reserve") {
    const base = days * dailyRate + kmPrice * meter;
    const gstAmount = ((base + advance) * GST) / 100;

    finalAmount = base + gstAmount - advance; // ✅ correct subtraction
  } else {
    finalAmount = totalPrice + (totalPrice * GST) / 100;
  }

  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handlePayment = () => {
    if (!user) {
      toast.error("Please login first");
      return;
    }

    try {
      const options = {
        // key: "rzp_test_SEuHWyEAvt82zR", // TestAPI
        key: RAZORPAY_KEY, // Main Razorpay Key
        amount: (finalAmount + finalAmount * 0.015) * 100, // ₹200 in paise
        currency: "INR",
        name: "DibruGo",
        description: "Booking Fee",
        handler: async function (response) {
          try {
            const paymentId = response.razorpay_payment_id || "";

            // 🔥 Update Firestore LiveBookings
            const bookingRef = doc(db, "LiveBookings", booking.id);

            await updateDoc(bookingRef, {
              payment_status: "paid",
              total_payment_id: paymentId,
              total_paid_amount: finalAmount + finalAmount * 0.015,
              total_paid_at: new Date(),
            });
            setShowPaymentModal(false);
            toast.success("Payment successful!");
          } catch (error) {
            console.error("Payment update error:", error);
            toast.error("Payment saved failed!");
          }
        },
        prefill: {
          name: booking.customer_name,
          email: user?.email || "",
          contact: booking.customer_phone,
        },
        theme: {
          color: "#0a0a0a",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      // console.error("Razorpay Error:", err);
      toast.error("Payment failed");
    }
  };

  if (!user) {
    return <Login handleGoogleLogin={handleGoogleLogin} />;
  }

  if (!loading && bookings.length > 1 && !booking) {
    return (
      <div className="container web-main" style={{ marginTop: "90px" }}>
        <h6 className="mb-2" style={{ color: "grey" }}>
          My Bookings
        </h6>

        {bookings.map((ride, index) => (
          <div
            key={ride.id}
            className="card highlight-card shadow-sm p-3 mb-2"
            style={{ cursor: "pointer" }}
            onClick={() => setBooking(ride)}
          >
            <p className="mb-0">
              <b>Pickup:</b> {ride.pickup}
            </p>
            <p className="mb-0">
              <b>Drop:</b> {ride.drop}
            </p>
            <p className="mb-0">
              Booking Type: <b>{ride.booking_type}</b>
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (!booking)
    return (
      <>
        <div className="container web-main p-4">
          <img
            style={{ height: "250px", width: "100%", objectFit: "cover" }}
            src="https://assets-v2.lottiefiles.com/a/a7e0e4fc-1185-11ee-8aab-2f2ff7d6373f/5liX9pQPAh.gif"
            className="img-fluid"
          />
          <p className="text-center" style={{ color: "grey" }}>
            No Bookings
          </p>
        </div>
      </>
    );

  return (
    <>
      <GoToTop />

      <div style={{ marginTop: "75px" }}>
        {booking?.pickup_lat &&
          booking?.pickup_lng &&
          booking?.drop_lat &&
          booking?.drop_lng && (
            <MapContainer
              center={[booking.pickup_lat, booking.pickup_lng]}
              zoom={13}
              zoomControl={true}
              style={{ height: "320px", width: "100%", zIndex: "10" }}
            >
              <TileLayer
                zIndex={1}
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />

              <Marker
                position={[booking.pickup_lat, booking.pickup_lng]}
                icon={L.divIcon({
                  className: "custom-marker pickup-marker",
                  iconSize: [20, 20],
                  html: `<div></div>`,
                })}
              >
                <Tooltip
                  direction="top"
                  offset={[0, -10]}
                  permanent
                  className="custom-tooltip pickup-tooltip"
                >
                  <div>
                    <b>Pickup</b>
                    <div>
                      {booking.pickup?.length > 40
                        ? booking.pickup.slice(0, 40) + "..."
                        : booking.pickup}
                    </div>
                  </div>
                </Tooltip>
              </Marker>

              <Marker
                position={[booking.drop_lat, booking.drop_lng]}
                icon={L.divIcon({
                  className: "custom-marker drop-marker",
                  iconSize: [20, 20],
                  html: `<div></div>`,
                })}
              >
                <Tooltip
                  direction="top"
                  offset={[0, -10]}
                  permanent
                  className="custom-tooltip drop-tooltip"
                >
                  <div>
                    <b>Drop</b>
                    <div>
                      {booking.drop?.length > 40
                        ? booking.drop.slice(0, 40) + "..."
                        : booking.drop}
                    </div>
                  </div>
                </Tooltip>
              </Marker>

              {directionsResult && (
                <>
                  <Polyline
                    positions={directionsResult}
                    pathOptions={{ color: "black", weight: 5 }}
                  />
                </>
              )}

              {directionsResult && <FitBounds coords={directionsResult} />}
            </MapContainer>
          )}
      </div>

      <div className="container">
        <div className="row">
          <div
            className="col-md-6"
            style={{ marginTop: "-50px", zIndex: "10" }}
          >
            <div className="uber-summary-card">
              <div className="uber-locations">
                <div className="location-row">
                  <div className="dot pickup-dot"></div>
                  <div className="location-text">
                    <span className="label">Pickup</span>
                    <span className="value">{booking.pickup}</span>
                  </div>
                </div>

                <div className="short-vertical-line"></div>

                <div className="location-row">
                  <div className="square drop-square"></div>
                  <div className="location-text">
                    <span className="label">Drop</span>
                    <span className="value">{booking.drop}</span>
                  </div>
                </div>
              </div>
              <FaPencilAlt
                className="edit-icon"
                onClick={() => setShowBookingSelector(true)}
              />
            </div>

            <div className="uber-date-card mt-2">
              <div className="uber-date-row">
                <div>
                  <div className="date-label">Pickup</div>
                  <div className="date-value">
                    {booking?.pickupTime
                      ?.toDate()
                      ?.toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                  </div>
                  <div className="date-sub">
                    {booking?.pickupTime
                      ?.toDate()
                      ?.toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                  </div>
                </div>
              </div>

              <div className="divider"></div>

              <div className="uber-date-row">
                <div>
                  <div className="date-label">Return</div>
                  <div className="date-value">
                    {booking?.booking_type == "Pick & Drop"
                      ? "--"
                      : getDropDateFromDays()}
                    <div className="date-sub" style={{ color: "red" }}>
                      {booking?.ride_end
                        ?.toDate()
                        ?.toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                    </div>
                  </div>
                  <div className="days-badge">
                    {/* ( {NoofDays} {NoofDays > 1 ? "Days" : "Day"} ) */}
                  </div>
                </div>
              </div>
            </div>

            {booking.status == 3 ? (
              <>
                <div className="card highlight-card shadow-sm p-3 mb-2">
                  <h6
                    style={{
                      color: "grey",
                      marginBottom: "0",
                      fontSize: "13px",
                    }}
                  >
                    Ride Started at :{" "}
                    <b style={{ color: "green" }}>
                      {formatTimestamp(booking.ride_start)}
                    </b>
                  </h6>
                </div>
              </>
            ) : null}

            <div className="uber-summary-card mb-3">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <p style={{ color: "grey", fontSize: "14px" }} className="mb-0">
                  Ride {booking.status < 1 ? "Rate" : "Summary"}
                </p>
                <div>
                  {booking.status == 4 ? (
                    <>
                      <span className="badge bg-success p-2">
                        Ride Completed
                      </span>

                      {booking.payment_status == "paid" ? (
                        <>
                          <span className="badge bg-success ms-2 p-2">
                            Paid
                          </span>
                        </>
                      ) : (
                        <span className="badge bg-danger ms-2 p-2">
                          Not Paid
                        </span>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
              {booking.confirm_price == 1 ? (
                <>
                  <h5 className="text-success mt-2 mb-0">
                    <strong>{booking.total_price}</strong>
                  </h5>
                </>
              ) : (
                <>
                  <div className="row">
                    {booking.booking_type == "Reserve" ? (
                      <>
                        {!booking.ride_end ? (
                          <p
                            className="mb-1 text-danger text-center"
                            style={{ fontSize: "13px" }}
                          >
                            Your final fare will be calculated once the trip is
                            completed.
                          </p>
                        ) : (
                          <>
                            <div className="col-8">
                              <small
                                className="mb-0"
                                style={{ fontSize: "12px" }}
                              >
                                Daily Charge{" "}
                                <i className="mb-0">
                                  ({" "}
                                  {calculateRideDays(
                                    booking.ride_start,
                                    booking.ride_end,
                                  )}{" "}
                                  Day(s) x ₹{booking.daily_rate} )
                                </i>{" "}
                                :
                              </small>
                            </div>
                            <div
                              style={{ textAlign: "right" }}
                              className="col-4"
                            >
                              <b style={{ fontSize: "14px" }}>
                                {formatINR(
                                  calculateRideDays(
                                    booking.ride_start,
                                    booking.ride_end,
                                  ) * booking.daily_rate,
                                )}
                              </b>
                            </div>

                            <div className="col-8">
                              <small style={{ fontSize: "12px" }}>
                                Total KM Rate{" "}
                                <i>
                                  ( ₹{booking.km_price} x{" "}
                                  {booking.meter_reading} )
                                </i>
                                :
                              </small>
                            </div>
                            <div
                              style={{ textAlign: "right" }}
                              className="col-4"
                            >
                              <span style={{ fontSize: "14px" }}>
                                {formatINR(
                                  booking.km_price * booking.meter_reading,
                                )}
                              </span>
                            </div>

                            <div className="col-8">
                              <small style={{ fontSize: "12px" }}>
                                GST ({GST}%) :
                              </small>
                            </div>
                            <div
                              style={{ textAlign: "right" }}
                              className="col-4"
                            >
                              <span style={{ fontSize: "14px" }}>
                                {formatINR(
                                  ((calculateRideDays(
                                    booking.ride_start,
                                    booking.ride_end,
                                  ) *
                                    booking.daily_rate +
                                    booking.km_price * booking.meter_reading +
                                    booking.preBookingFee) *
                                    GST) /
                                    100,
                                )}
                              </span>
                            </div>

                            <div className="col-8">
                              <small style={{ fontSize: "12px" }}>
                                Advance Amount :
                              </small>
                            </div>
                            <div
                              style={{ textAlign: "right" }}
                              className="col-4"
                            >
                              <i style={{ fontSize: "14px" }}>
                                - {formatINR(booking.preBookingFee)}
                              </i>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="col-8">
                          <small className="mb-0" style={{ fontSize: "12px" }}>
                            Ride Charge :
                          </small>
                        </div>
                        <div style={{ textAlign: "right" }} className="col-4">
                          <span style={{ fontSize: "14px" }}>
                            {formatINR(booking.total_price)}
                          </span>
                        </div>

                        <div className="col-8">
                          <small className="mb-0" style={{ fontSize: "12px" }}>
                            GST <i>( {GST}% )</i> :
                          </small>
                        </div>
                        <div style={{ textAlign: "right" }} className="col-4">
                          <span style={{ fontSize: "14px" }}>
                            {formatINR((booking.total_price * GST) / 100)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <hr className="mb-2 mt-1" />
                  {/* Grand Total */}
                  <div className="d-flex justify-content-between ">
                    <h6 className="mb-0 fw-bold">Grand Total</h6>
                    <div>
                      <h4 className="mb-0 fw-bold text-success">
                        {formatINR(finalAmount)}
                      </h4>
                      {booking.status >= 1 &&
                      booking.payment_status !== "paid" ? (
                        <>
                          <button
                            onClick={() => setShowPaymentModal(true)}
                            className="btn btn-dark btn-sm mt-2 w-100"
                            style={{ cursor: "pointer" }}
                          >
                            Pay Now
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
              {/* Fare Breakdown */}
            </div>
          </div>
          <div className="col-md-6">
            {booking.veh_number ? (
              <>
                <div
                  className="uber-summary-card mb-2 mt-2"
                  style={{
                    borderRadius: "18px",
                    background: "#fff",
                    padding: "16px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    {/* LEFT SIDE */}
                    <div>
                      {/* Vehicle Name */}
                      <h6 className="mb-1 fw-semibold">
                        <span style={{ textTransform: "capitalize" }}>
                          {booking.veh_color}
                        </span>{" "}
                        {vehicleDetails?.name || ""}
                      </h6>

                      {/* Vehicle Number Plate */}
                      <div
                        style={{
                          display: "inline-block",
                          background: "#f1f1f1",
                          padding: "4px 10px",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: "700",
                          letterSpacing: "0.9px",
                          // marginBottom: "6px",
                        }}
                      >
                        {booking.veh_number}
                      </div>
                    </div>

                    {/* RIGHT SIDE IMAGE */}
                    <img
                      src={vehicleDetails?.img_url}
                      alt="vehicle"
                      width={120}
                      style={{
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>

                {driverDetails?.name ? (
                  <>
                    <div
                      className="uber-summary-card mb-2"
                      style={{
                        borderRadius: "16px",
                        background: "#fff",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        {/* Left Side */}
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={driverDetails?.profile_img || driver}
                            width={50}
                            height={50}
                            style={{
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                            alt="driver"
                          />

                          <div>
                            <h6 className="mb-1 fw-semibold">
                              {driverDetails?.name || "Driver"}
                            </h6>

                            <div className="d-flex align-items-center gap-1 text-muted">
                              <IoMdStarOutline size={16} />
                              <span style={{ fontSize: "14px" }}>4.87</span>
                            </div>
                          </div>
                        </div>

                        {/* Call Button */}
                        <div
                          onClick={() =>
                            (window.location.href = `tel:${driverDetails?.phone}`)
                          }
                          style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "50%",
                            background: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#333")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "#000")
                          }
                        >
                          <FaPhoneAlt color="#fff" size={14} />
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <h4
                  className="text-center mt-3 mb-0 animated-text"
                  style={{
                    color: "#ff7a00",
                    fontWeight: "600",
                    fontSize: "19px",
                  }}
                >
                  Finding your ride...
                </h4>
                <p
                  className="text-center mb-2 animated-text"
                  style={{ color: "grey", fontSize: "13px" }}
                >
                  We'll notify you once a driver is assigned
                </p>
                <img
                  style={{ borderRadius: "8px" }}
                  src="https://cdn.dribbble.com/users/5112840/screenshots/18309779/media/0e25205a743b03bcd3db3c1927d4ddfe.gif"
                  className="img-fluid"
                />
              </>
            )}
          </div>
        </div>

        {booking.status == 2 ? (
          <>
            {booking.isScheduled ? (
              <p
                style={{ fontSize: "12px", color: "red", textAlign: "center" }}
              >
                Please ensure the vehicle trip meter is set to{" "}
                <strong>0</strong> before starting the TRIP
              </p>
            ) : null}
          </>
        ) : null}

        <button
          className="btn btn-secondary w-100 my-3 py-2"
          onClick={() => setShowHelpModal(true)}
        >
          Need Help?
        </button>
      </div>

      {showHelpModal && (
        <div
          className="bottom-modal-overlay"
          onClick={() => setShowHelpModal(false)}
        >
          <div className="bottom-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Need Help?</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowHelpModal(false)}
                ></button>
              </div>

              <div className="modal-body mt-3">
                <p className="mb-2">How can we assist you?</p>

                <div className="d-grid gap-2">
                  {driverDetails?.name ? (
                    <button
                      className="btn btn-outline-dark"
                      onClick={() =>
                        (window.location.href = `tel:${driverDetails?.phone}`)
                      }
                    >
                      <FaPhoneAlt className="me-2" />
                      Call Driver
                    </button>
                  ) : null}

                  <button
                    onClick={() => (window.location.href = `tel:7576825040`)}
                    className="btn btn-outline-secondary"
                  >
                    <FaPhoneAlt className="me-2" /> Call Support
                  </button>

                  <button className="btn btn-outline-secondary">
                    <RiMessage2Fill className="me-2" /> Chat Support
                  </button>

                  {booking.status < 3 ? (
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => handleCancelRide(booking)}
                    >
                      Cancel Ride
                    </button>
                  ) : (
                    <button
                      disabled
                      style={{ cursor: "not-allowed" }}
                      className="btn btn-outline-danger"
                    >
                      Cancel Ride
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBookingSelector && (
        <div
          className="bottom-modal-overlay"
          onClick={() => setShowBookingSelector(false)}
        >
          <div className="bottom-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Active Ride</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowBookingSelector(false)}
                ></button>
              </div>

              <div className="modal-body mt-3">
                {bookings.map((ride) => (
                  <div
                    key={ride.id}
                    className={`card highlight-card p-3 mb-2 shadow-sm ${
                      booking?.id === ride.id ? "border-primary" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setBooking(ride);
                      setShowBookingSelector(false);
                    }}
                  >
                    <p className="mb-0">
                      <b>Pickup:</b> {ride.pickup}
                    </p>
                    <p className="mb-0">
                      <b>Drop:</b> {ride.drop}
                    </p>
                    <p className="mb-0">
                      Booking Type: <b>{ride.booking_type}</b>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && (
        <div
          className="bottom-modal-overlay"
          onClick={() => setShowPaymentModal(false)}
        >
          <div className="bottom-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h4>Complete Payment</h4>
                  <p class="booking-id">
                    Booking ID: #
                    <span style={{ textTransform: "uppercase" }}>
                      {booking.id}
                    </span>
                  </p>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPaymentModal(false)}
                ></button>
              </div>

              <div class="payment-modal">
                {/* <!-- Card --> */}
                <div class="payment-card">
                  <p class="pay-amount-label mb-1">Amount to Pay</p>
                  <h1 class="pay-amount">
                    {formatINR(finalAmount + finalAmount * 0.015)}
                  </h1>

                  <div class="fee-badge">
                    Includes {formatINR(finalAmount * 0.015)} processing fee
                  </div>

                  <p class="secure-text">
                    <CiLock className="icon" /> Secure payment powered by
                    Razorpay
                  </p>

                  <button class="pay-btn" onClick={handlePayment}>
                    Proceed to Pay •{" "}
                    {formatINR(finalAmount + finalAmount * 0.015)}
                  </button>

                  {/* <!-- Payment Icons --> */}
                  <div class="payment-methods">
                    <img src={payImg} className="img-fluid" />
                  </div>

                  <p class="pay-methods-text">
                    Multiple payment methods available
                  </p>
                </div>
                {/* 
    <!-- Footer --> */}
                <div class="payment-footer">
                  <span>
                    <FaShieldHalved className="icon" /> Secure
                  </span>
                  <span>
                    <FaBoltLightning className="icon" /> Instant
                  </span>
                  <span>
                    <FaLock className="icon" /> Encrypted
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RideDetails;
